import { z } from 'zod';
import type React from 'react';

// Apoio stateless do InventoryDetail (régua de 400 linhas).
export const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Ativo',
  LOW_STOCK: 'Estoque baixo',
  OUT_OF_STOCK: 'Sem estoque',
  EXPIRED: 'Vencido',
  INACTIVE: 'Inativo',
  ARCHIVED: 'Arquivado',
};

export const STATUS_VARIANTS: Record<string, string> = {
  ACTIVE: 'success',
  LOW_STOCK: 'warning',
  OUT_OF_STOCK: 'danger',
  EXPIRED: 'danger',
  INACTIVE: 'default',
  ARCHIVED: 'default',
};

export const VISIBILITY_LABELS: Record<string, string> = {
  PUBLIC: 'Público',
  INTERNAL: 'Interno',
  RESTRICTED: 'Restrito',
};

export const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  INCOMING: 'Entrada',
  OUTGOING: 'Saída',
  ADJUSTMENT: 'Ajuste',
  LOSS_DISPOSAL: 'Perda/Descarte',
};

export const MOVEMENT_TYPE_VARIANTS: Record<string, string> = {
  INCOMING: 'success',
  OUTGOING: 'danger',
  ADJUSTMENT: 'info',
  LOSS_DISPOSAL: 'warning',
};

export const editFormSchema = z.object({
  name: z.string().min(1).max(255),
  unit: z.string(),
  minimumQuantity: z.string().optional(),
  categoryId: z.string().optional(),
  locationId: z.string().optional(),
  description: z.string().optional(),
  visibility: z.string(),
  isPerishable: z.boolean(),
  requiresHouseValidation: z.boolean(),
  controlledOrSensitive: z.boolean(),
  expiryDate: z.string().optional(),
});

export type EditForm = z.infer<typeof editFormSchema>;

export const movementFormSchema = z.object({
  type: z.string().min(1),
  quantity: z.string().min(1, 'Quantidade é obrigatória'),
  reason: z.string().optional(),
});

export type MovementForm = z.infer<typeof movementFormSchema>;

export type Tab = 'dados' | 'movimentacoes';

export function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.8rem' }}>
      <span style={{ color: 'var(--ink)', fontWeight: 600 }}>{label}</span>
      {children}
      {error && <span style={{ color: 'var(--danger)', fontSize: '0.7rem' }}>{error}</span>}
    </label>
  );
}

export function SkeletonDetail() {
  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 900 }}>
      <div
        style={{
          width: 140,
          height: 14,
          background: 'var(--line-soft)',
          borderRadius: 8,
          marginBottom: '1.5rem',
        }}
      />
      <div
        style={{
          width: '60%',
          height: 24,
          background: 'var(--line-soft)',
          borderRadius: 8,
          marginBottom: '2rem',
        }}
      />
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          style={{
            background: '#fff',
            borderRadius: 14,
            border: '1px solid var(--line)',
            padding: '1.25rem 1.5rem',
            height: 100,
            marginBottom: '1rem',
          }}
        >
          <div
            style={{
              width: '30%',
              height: 14,
              background: 'var(--line-soft)',
              borderRadius: 8,
              marginBottom: '0.75rem',
            }}
          />
          <div
            style={{
              width: '60%',
              height: 12,
              background: 'var(--soft)',
              borderRadius: 8,
              marginBottom: '0.5rem',
            }}
          />
          <div style={{ width: '40%', height: 12, background: 'var(--soft)', borderRadius: 8 }} />
        </div>
      ))}
    </div>
  );
}

export const dlGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '180px 1fr',
  gap: '0.5rem 1rem',
  fontSize: '0.875rem',
};

export const Dt = ({ children }: { children: React.ReactNode }) => (
  <dt style={{ color: 'var(--ink)', fontWeight: 600, margin: 0 }}>{children}</dt>
);

export const Dd = (props: React.HTMLAttributes<HTMLElement> & { children: React.ReactNode }) => (
  <dd style={{ margin: 0, color: 'var(--ink)', ...props.style }}>{props.children}</dd>
);

export const inputCss: React.CSSProperties = {
  width: '100%',
  padding: '0.5rem 0.6rem',
  border: '1px solid var(--line)',
  borderRadius: 10,
  fontSize: '0.85rem',
  boxSizing: 'border-box',
  outline: 'none',
};

export const primaryBtn: React.CSSProperties = {
  background: 'var(--green)',
  color: '#fff',
  border: 'none',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
};

export const secondaryBtn: React.CSSProperties = {
  background: '#fff',
  color: 'var(--muted)',
  border: '1px solid var(--line)',
  borderRadius: 999,
  padding: '0.45rem 1rem',
  fontSize: '0.8rem',
  cursor: 'pointer',
};

export const linkBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'var(--muted)',
  cursor: 'pointer',
  fontSize: '0.875rem',
  marginBottom: '0.5rem',
  display: 'block',
  padding: 0,
};

export const modalOverlay: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--overlay)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100,
};
